import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Link,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import WarningIcon from '@mui/icons-material/Warning';

import {
  getInspectionCheck,
  updateInspectionCheck,
} from '@/actions/inspectionCheck';
import DocumentViewDialog from '@/components/shared/DocumentViewDialog';
import { useApi } from '@/providers/ApiContext';
import type {
  EntityId,
  InspectionCheck,
  InspectionCheckOverrideType,
  InspectionCheckUpdateRequest,
} from '@/types';

import RatingField from './RatingField';
import OverrideTypeField from './OverrideTypeField';
import JustificationField from './JustificationField';

const drawer_width = 480;

function read_optional_string(source: unknown, ...keys: string[]): string {
  if (!source || typeof source !== 'object') {
    return '';
  }
  const record = source as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string') {
      return value;
    }
  }
  return '';
}

interface InspectionCheckReviewDrawerProps {
  open: boolean;
  onClose: () => void;
  inspection_entity_id: EntityId;
  active_check: InspectionCheck | null;
  manifest_label: string;
  reference_document_title: string;
  reference_document_content: string | undefined;
  has_previous: boolean;
  has_next: boolean;
  on_previous: () => void;
  on_next: () => void;
  on_saved: (check: InspectionCheck) => void;
  on_error: (message: string) => void;
}

function InspectionCheckReviewDrawer({
  open,
  onClose,
  inspection_entity_id,
  active_check,
  manifest_label,
  reference_document_title,
  reference_document_content,
  has_previous,
  has_next,
  on_previous,
  on_next,
  on_saved,
  on_error,
}: InspectionCheckReviewDrawerProps): React.JSX.Element {
  const api = useApi();
  const theme = useTheme();
  const is_mobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [rating, set_rating] = useState<boolean | null>(null);
  const [override_type, set_override_type] = useState<
    InspectionCheckOverrideType | ''
  >('');
  const [justification, set_justification] = useState<string>('');
  const [loading, set_loading] = useState<boolean>(false);
  const [detail_check, set_detail_check] = useState<InspectionCheck | null>(
    null,
  );

  const [discard_dialog_open, set_discard_dialog_open] =
    useState<boolean>(false);
  const [discard_action, set_discard_action] = useState<
    'close' | 'previous' | 'next' | 'reset' | null
  >(null);

  const [reference_dialog_open, set_reference_dialog_open] =
    useState<boolean>(false);

  const display_check: InspectionCheck | null = detail_check ?? active_check;

  useEffect(() => {
    if (!open) {
      set_reference_dialog_open(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !active_check || !api) {
      set_detail_check(null);
      return;
    }
    let cancelled = false;
    set_detail_check(null);
    getInspectionCheck(api, inspection_entity_id, active_check.entity_id)
      .then((full) => {
        if (!cancelled) {
          set_detail_check(full);
        }
      })
      .catch(() => {
        if (!cancelled) {
          set_detail_check(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, active_check?.entity_id, inspection_entity_id, api, active_check]);

  const reset_from_check = useCallback((check: InspectionCheck | null) => {
    if (!check) {
      set_rating(null);
      set_override_type('');
      set_justification('');
      return;
    }
    const r = check.rating;
    set_rating(typeof r === 'boolean' ? r : null);
    set_override_type(
      (check.override_type as InspectionCheckOverrideType) || '',
    );
    set_justification(check.justification || '');
  }, []);

  useEffect(() => {
    if (!display_check) {
      reset_from_check(null);
      return;
    }
    reset_from_check(display_check);
  }, [display_check, reset_from_check]);

  const is_dirty = useCallback((): boolean => {
    if (!display_check) {
      return false;
    }
    const orig_rating =
      typeof display_check.rating === 'boolean' ? display_check.rating : null;
    const orig_type =
      (display_check.override_type as InspectionCheckOverrideType) || '';
    const orig_just = display_check.justification || '';
    return (
      rating !== orig_rating ||
      override_type !== orig_type ||
      justification !== orig_just
    );
  }, [display_check, rating, override_type, justification]);

  const dirty = is_dirty();

  const reload_from_database = useCallback(async (): Promise<void> => {
    if (!api || !active_check) {
      reset_from_check(null);
      set_detail_check(null);
      return;
    }
    try {
      const full = await getInspectionCheck(
        api,
        inspection_entity_id,
        active_check.entity_id,
      );
      set_detail_check(full);
      reset_from_check(full);
    } catch (_error) {
      // Ignore refresh errors; keep current local values.
    }
  }, [api, active_check, inspection_entity_id, reset_from_check]);

  const request_discard = useCallback(
    (action: 'close' | 'previous' | 'next' | 'reset') => {
      if (!dirty) {
        if (action === 'close') {
          onClose();
        } else if (action === 'previous') {
          on_previous();
        } else if (action === 'next') {
          on_next();
        } else {
          void reload_from_database();
        }
        return;
      }
      set_discard_action(action);
      set_discard_dialog_open(true);
    },
    [dirty, onClose, on_previous, on_next, reload_from_database],
  );

  const handle_discard_cancel = useCallback(() => {
    set_discard_dialog_open(false);
    set_discard_action(null);
  }, []);

  const handle_discard_confirm = useCallback(() => {
    const action = discard_action;
    set_discard_dialog_open(false);
    set_discard_action(null);
    if (!action) {
      return;
    }
    if (action === 'previous') {
      on_previous();
      return;
    }
    if (action === 'next') {
      on_next();
      return;
    }
    // For close/reset: discard edits but keep the drawer open by refreshing from DB.
    void reload_from_database();
  }, [discard_action, on_previous, on_next, reload_from_database]);

  const build_payload = useCallback((): InspectionCheckUpdateRequest | null => {
    if (rating === null) {
      return null;
    }
    if (rating === true) {
      return { rating: true };
    }
    if (!override_type) {
      return null;
    }
    if (!justification.trim()) {
      return null;
    }
    return {
      rating: false,
      override_type,
      justification: justification.trim(),
    };
  }, [rating, override_type, justification]);

  const payload_ready = useCallback(
    (): boolean => Boolean(build_payload()),
    [build_payload],
  );

  const handle_save = useCallback(async (): Promise<void> => {
    if (!api || !display_check) {
      return;
    }
    const payload = build_payload();
    if (!payload) {
      on_error(
        'Choose Approve or Reject. For Reject, select a reason and enter the required details.',
      );
      return;
    }
    set_loading(true);
    try {
      const updated = await updateInspectionCheck(
        api,
        inspection_entity_id,
        display_check.entity_id,
        payload,
      );
      set_detail_check(updated);
      on_saved(updated);
      reset_from_check(updated);
    } catch (error) {
      on_error(
        (error as Error).message ||
          'Unable to save check feedback. This could be due to network issues or server problems.',
      );
    } finally {
      set_loading(false);
    }
  }, [
    api,
    display_check,
    build_payload,
    inspection_entity_id,
    on_error,
    on_saved,
    reset_from_check,
  ]);

  const rationale_text = read_optional_string(display_check, 'rationale');
  const suggestion_text = read_optional_string(display_check, 'suggestion');
  const section_value = read_optional_string(display_check, 'check_section');
  const show_section =
    Boolean(section_value.trim()) &&
    section_value.trim().toLowerCase() !== 'global';
  const has_reference_document_content = Boolean(
    reference_document_content?.trim(),
  );

  return (
    <>
      <DocumentViewDialog
        title={reference_document_title || 'Reference document'}
        contents={reference_document_content || ''}
        open={reference_dialog_open}
        handleClose={() => set_reference_dialog_open(false)}
      />
      <Dialog
        open={discard_dialog_open}
        onClose={handle_discard_cancel}
        aria-labelledby='discard-changes-title'
      >
        <DialogTitle id='discard-changes-title'>Discard changes?</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            You have unsaved changes. If you continue, they will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handle_discard_cancel} variant='outlined'>
            Keep editing
          </Button>
          <Button
            onClick={handle_discard_confirm}
            variant='contained'
            color='error'
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor='right'
        open={open}
        onClose={() => request_discard('close')}
        slotProps={{
          paper: {
            sx: {
              width: is_mobile ? '100vw' : drawer_width,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              maxHeight: '100vh',
              borderRadius: 0,
            },
          },
        }}
        sx={{ zIndex: (theme) => theme.zIndex.drawer }}
      >
        <Toolbar
          sx={{
            flexShrink: 0,
            minHeight: '55px !important',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography variant='h5'>Evaluation Details</Typography>
          <Tooltip title='Close'>
            <Box component='span'>
              <IconButton
                aria-label='Close drawer'
                onClick={() => request_discard('close')}
                disabled={loading}
                size='medium'
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Tooltip>
        </Toolbar>

        <Divider sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
          {!display_check ? (
            <Typography variant='body2'>No check selected.</Typography>
          ) : (
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant='subtitle1'>Criteria</Typography>
                <Divider />
              </Grid>

              <Grid size={12}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Manifest
                </Typography>
                <Typography variant='body2'>{manifest_label}</Typography>
              </Grid>
              {reference_document_title ? (
                <Grid size={12}>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Reference document
                  </Typography>
                  <Typography variant='body2'>
                    {reference_document_title ? (
                      has_reference_document_content ? (
                        <Link
                          component='button'
                          type='button'
                          underline='always'
                          onClick={() => {
                            set_reference_dialog_open(true);
                          }}
                          sx={{ cursor: 'pointer', font: 'inherit' }}
                        >
                          {reference_document_title}
                        </Link>
                      ) : (
                        reference_document_title
                      )
                    ) : (
                      '—'
                    )}
                  </Typography>
                </Grid>
              ) : undefined}
              {show_section ? (
                <Grid size={12}>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Section
                  </Typography>
                  <Typography variant='body2'>{section_value}</Typography>
                </Grid>
              ) : undefined}
              <Grid size={12}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Name
                </Typography>
                <Typography variant='body2'>
                  {read_optional_string(display_check, 'check_name', 'name') ||
                    '—'}
                </Typography>
              </Grid>
              {Boolean(active_check?.check_details) ? (
                <Grid size={12}>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Reference
                  </Typography>
                  <Typography variant='body2'>
                    {active_check?.check_details}
                  </Typography>
                </Grid>
              ) : undefined}

              <Grid size={12}>
                <Typography variant='subtitle1'>Evaluation Results</Typography>
                <Divider />
              </Grid>
              <Grid size={12}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Compliant
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {display_check?.compliant === true ? (
                    <CheckCircleIcon color='success' />
                  ) : display_check?.compliant === false ? (
                    <HighlightOffIcon color='error' />
                  ) : (
                    <WarningIcon color='warning' />
                  )}
                </Box>
              </Grid>
              <Grid size={12}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Rationale
                </Typography>
                <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                  {rationale_text || '—'}
                </Typography>
              </Grid>
              {suggestion_text.trim() ? (
                <Grid size={12}>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Suggestion
                  </Typography>
                  <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                    {suggestion_text}
                  </Typography>
                </Grid>
              ) : undefined}

              <Grid size={12}>
                <Typography variant='subtitle1'>Feedback</Typography>
                <Divider />
              </Grid>

              <Grid size={12}>
                <RatingField
                  value={rating}
                  onChange={(v) => set_rating(v)}
                  disabled={loading}
                />
              </Grid>

              {rating === false ? (
                <>
                  <Grid size={12}>
                    <OverrideTypeField
                      value={override_type}
                      onChange={(v) => set_override_type(v)}
                      disabled={loading}
                    />
                  </Grid>
                  {override_type ? (
                    <Grid size={12}>
                      <JustificationField
                        value={justification}
                        onChange={set_justification}
                        disabled={loading}
                      />
                    </Grid>
                  ) : undefined}
                </>
              ) : undefined}
            </Grid>
          )}
        </Box>

        <Divider sx={{ flexShrink: 0 }} />
        <Toolbar
          sx={{
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 2,
            minHeight: '56px !important',
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              variant='outlined'
              disabled={!has_previous || loading}
              onClick={() => request_discard('previous')}
            >
              Previous
            </Button>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Tooltip title='Cancel'>
              <Box component='span'>
                <IconButton
                  color='inherit'
                  onClick={() => request_discard('reset')}
                  disabled={loading || !dirty}
                  aria-label='Cancel'
                  size='medium'
                >
                  <UndoIcon />
                </IconButton>
              </Box>
            </Tooltip>
            <Tooltip title='Save'>
              <Box component='span'>
                <LoadingButton
                  variant='contained'
                  color='primary'
                  loading={loading}
                  onClick={() => void handle_save()}
                  disabled={
                    !display_check || loading || !dirty || !payload_ready()
                  }
                  aria-label='Save'
                  sx={{ minWidth: 48, px: 1.5 }}
                >
                  <SaveOutlinedIcon />
                </LoadingButton>
              </Box>
            </Tooltip>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant='outlined'
              disabled={!has_next || loading}
              onClick={() => request_discard('next')}
            >
              Next
            </Button>
          </Box>
        </Toolbar>
      </Drawer>
    </>
  );
}

export default InspectionCheckReviewDrawer;
